import { useEffect, useMemo, useState } from 'react';
import { DataTable, useDataTable, type DataTableFilter } from '@odo/shared-ui';
import { useVendorList } from '../../../hooks/useVendors';
import { useMyPermissions } from '../../../hooks/useMyPermissions';
import type { VendorListItem } from '../../../services/vendors';
import { buildVendorColumns, type VendorsSortBy } from './vendorColumns';
import { CreateVendorModal } from './CreateVendorModal';
import { VendorDrawer } from './VendorDrawer';
import { undispatchableVendors, vendorDispatchBlock } from './vendorDispatch';
import { VendorsUndispatchableBanner } from './VendorsUndispatchableBanner';
import { VendorsErrorState } from './VendorsErrorState';

// One request returns every vendor, so search, filter and sort are all local
// here - unlike every other billing table.

type VendorFilters = { state: string[] };

const STATE_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'retired', label: 'Retired' },
    // Retired vendors classify as 'retired' first, so this only ever matches
    // active ones - the label says so rather than implying otherwise.
    { value: 'unusable', label: 'Active, not orderable' }
];

// The list ships no recipient addresses, so search covers name and slug only.
function matchesSearch(vendor: VendorListItem, term: string): boolean {
    if (term.length === 0) return true;
    return [vendor.name, vendor.id].join(' ').toLowerCase().includes(term);
}

function matchesState(vendor: VendorListItem, states: readonly string[]): boolean {
    if (states.length === 0) return true;
    const block = vendorDispatchBlock(vendor);
    return states.some((state) =>
        state === 'active'
            ? vendor.isActive
            : state === 'retired'
              ? !vendor.isActive
              : block === 'no_methods' || block === 'no_usable_method'
    );
}

function compareVendors(a: VendorListItem, b: VendorListItem, by: VendorsSortBy): number {
    if (by === 'methods') return a.orderMethods.usable - b.orderMethods.usable;
    return a.name.localeCompare(b.name);
}

export interface VendorsContentProps {
    // Drawer state is pluggable: the org page drives it from the route so a
    // vendor is deep-linkable, an embedded instance would use local state.
    openVendorId: string | null;
    onOpenVendor: (id: string) => void;
    onCloseVendor: () => void;
    stateFilterStorageKey: string;
    onTotalCountChange?: (count: number) => void;
}

export function VendorsContent({
    openVendorId,
    onOpenVendor,
    onCloseVendor,
    stateFilterStorageKey,
    onTotalCountChange
}: VendorsContentProps) {
    const { vendors, isLoading, isFetching, isError, refetch } = useVendorList();
    const { hasPermission } = useMyPermissions();
    const canEdit = hasPermission('billing.update');

    const table = useDataTable<VendorsSortBy, VendorFilters>({
        defaultSort: { by: 'name', order: 'asc' },
        defaultFilters: { state: [] },
        mode: 'paged',
        filterStorageKeys: { state: stateFilterStorageKey }
    });

    const [search, setSearch] = useState('');
    const [showCreate, setShowCreate] = useState(false);

    const term = search.trim().toLowerCase();
    const { state } = table.filters;
    const { sort, order } = table;

    const rows = useMemo(() => {
        const filtered = vendors.filter(
            (vendor) => matchesSearch(vendor, term) && matchesState(vendor, state)
        );
        const direction = order === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => compareVendors(a, b, sort) * direction);
    }, [vendors, term, state, sort, order]);

    const notOrderable = useMemo(() => undispatchableVendors(vendors), [vendors]);

    // Same set the banner names, so tint and banner can never disagree.
    const notOrderableIds = useMemo(
        () => new Set(notOrderable.map((vendor) => vendor.id)),
        [notOrderable]
    );

    const totalCount = vendors.length;
    useEffect(() => {
        onTotalCountChange?.(totalCount);
    }, [totalCount, onTotalCountChange]);

    const columns = useMemo(() => buildVendorColumns(), []);

    const filters: DataTableFilter[] = [
        {
            key: 'state',
            label: 'State',
            multiple: true,
            options: STATE_OPTIONS,
            value: state,
            onChange: (value) => table.setFilter('state', value),
            workQueueVariant: true,
            align: 'right'
        }
    ];

    if (isError) {
        return (
            <div className="flex flex-col h-full overflow-hidden bg-gray-100">
                <VendorsErrorState
                    onRetry={() => {
                        void refetch();
                    }}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <div className="shrink-0 pt-6">
                <VendorsUndispatchableBanner vendors={notOrderable} onOpenVendor={onOpenVendor} />
            </div>

            <DataTable
                label="Vendors"
                columns={columns}
                rows={rows}
                getRowKey={(vendor) => vendor.id}
                rowHeightClassName="h-16"
                rowAction={{ onActivate: (vendor) => onOpenVendor(vendor.id), mode: 'click' }}
                // Red is reserved for "active but cannot be ordered from" - a
                // deliberate retirement is muted gray, not an alarm.
                rowClassName={(vendor) =>
                    notOrderableIds.has(vendor.id)
                        ? 'bg-red-50/40'
                        : !vendor.isActive
                          ? 'bg-gray-50 opacity-60'
                          : undefined
                }
                sort={{ by: sort, order, onSort: table.toggleSort }}
                isLoading={isLoading}
                emptyState="No vendors found."
                className="overflow-hidden px-8 pb-8"
                search={{
                    value: search,
                    onChange: setSearch,
                    placeholder: 'Search name or slug'
                }}
                filters={filters}
                toolbar={
                    isFetching && !isLoading ? (
                        <span className="text-xs text-gray-400">Refreshing…</span>
                    ) : undefined
                }
                actions={
                    canEdit
                        ? [
                              {
                                  key: 'new-vendor',
                                  label: '+ New vendor',
                                  variant: 'primary',
                                  onClick: () => setShowCreate(true)
                              }
                          ]
                        : undefined
                }
            />

            {openVendorId && <VendorDrawer vendorId={openVendorId} onClose={onCloseVendor} />}

            {showCreate && (
                <CreateVendorModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(id) => {
                        setShowCreate(false);
                        onOpenVendor(id);
                    }}
                />
            )}
        </div>
    );
}
